// Speech queue bookkeeping lives outside Vuex `state` (module-scoped, not
// reactive) because it holds Promise resolve/reject callbacks per queued
// speech, which don't belong in reactive/serializable store state. Only
// `isSpeaking`/`textBubbleText` (what the UI actually renders) live in state.
let speechQueue = [];
let isSpeechQueueBusy = false;
let currentSpeechCallbacks = null;

export default {
  state: {
    avatarLoaded: false,
    currentLanguage: "en",
    avatarSpeech: "",
    textBubbleText: "",
    isSpeaking: false,
    defaultBubbleText: false,
    count: 0,
    avatarLangCodes: null,
    avatarDetails: null,
  },
  getters: {
    getCurrentLanguage: (state) => state.currentLanguage,
    getAvatarSpeech: (state) => state.avatarSpeech,
    getTextBubbleText: (state) => state.textBubbleText,
    getIsSpeaking: (state) => state.isSpeaking,
    getAvatarLoaded: (state) => state.avatarLoaded,
    getDefaultBubbleText: (state) => state.defaultBubbleText,
    getAvatarLangCodes: (state) => state.avatarLangCodes,
    getAvatarDetails: (state) => state.avatarDetails,
  },
  mutations: {
    setCurrentLanguage(state, lang) {
      state.currentLanguage = lang;
    },
    setIsSpeaking(state, status) {
      state.isSpeaking = status;
    },
    setAvatarSpeech(state, speech) {
      state.avatarSpeech = speech;
    },
    clearAvatarSpeech(state) {
      state.avatarSpeech = "";
    },
    setTextBubbleText(state, text) {
      state.textBubbleText = text;
    },
    clearTextBubbleText(state) {
      state.textBubbleText = "";
    },
    setAvatarLoaded(state, status) {
      state.avatarLoaded = status;
    },
    setDefaultBubbleText(state, status) {
      state.defaultBubbleText = status;
    },
    setAvatarLangCodes(state, langCodeObj) {
      state.avatarLangCodes = langCodeObj;
    },
    setAvatarDetails(state, avatarObj) {
      state.avatarDetails = avatarObj;
    },
  },
  actions: {
    // Speeches are queued and spoken one at a time so the on-screen text
    // bubble always matches what's actually being spoken, and so callers can
    // `await` this action to know exactly when THIS speech (not just "some"
    // speech) has finished playing — e.g. to close a dialog only once its
    // own confirmation message has been heard. The promise resolves once the
    // speech is done, one way or another (played to completion via
    // avatar.vue's vh_talkEnded/completeCurrentSpeech, stopped/interrupted,
    // or skipped because there's no network) so the many existing
    // fire-and-forget callers never see an unhandled rejection. It only
    // rejects for genuinely invalid input (no speech text given).
    avatarSpeak({ getters, dispatch }, speech) {
      return new Promise((resolve, reject) => {
        if (speech === null || speech === "") {
          return reject("Nothing to speak");
        }
        if (!getters.getNetworkConnection) {
          // Not an error the many fire-and-forget callers need to `.catch` —
          // just nothing to wait for. Resolve so an `await`ing caller isn't
          // blocked by a speech that will never happen.
          return resolve("Skipped speech: no network connection");
        }
        speechQueue.push({ speech, resolve, reject });
        dispatch("processSpeechQueue");
      });
    },
    processSpeechQueue({ getters, commit }) {
      if (isSpeechQueueBusy || speechQueue.length === 0) {
        return;
      }
      const next = speechQueue.shift();
      isSpeechQueueBusy = true;
      currentSpeechCallbacks = next;
      commit("clearAvatarSpeech");
      setTimeout(() => {
        commit("setTextBubbleText", next.speech);
        try {
          window.sayText(
            next.speech,
            getters.getAvatarLangCodes.voiceId,
            getters.getAvatarLangCodes.langId,
            getters.getAvatarLangCodes.engineId
          );
        } catch (error) {
          isSpeechQueueBusy = false;
          currentSpeechCallbacks = null;
          // Resolve rather than reject: a sayText failure shouldn't surface
          // as an unhandled rejection to the many callers that don't attach
          // a .catch(); it should just let the queue (and any awaiter) move on.
          next.resolve("Speech failed to start: " + error.message);
        }
      }, 100);
    },
    // Called by avatar.vue's vh_talkEnded once SitePal reports the current
    // utterance actually finished playing. Resolves that speech's promise
    // and advances to whatever's queued next.
    completeCurrentSpeech({ dispatch }) {
      const finished = currentSpeechCallbacks;
      isSpeechQueueBusy = false;
      currentSpeechCallbacks = null;

      if (finished) {
        finished.resolve("Speech complete");
      }

      dispatch("processSpeechQueue");
    },
    // Called when speech is deliberately stopped (navigation away, a new
    // flow interrupting the avatar, etc). Drops anything queued and resolves
    // (not rejects — this isn't an error) the in-flight speech's promise so
    // an `await`ing caller isn't left hanging forever.
    clearSpeechQueue() {
      const queued = speechQueue;
      speechQueue = [];

      queued.forEach(item => item.resolve("Speech stopped"));

      if (currentSpeechCallbacks) {
        currentSpeechCallbacks.resolve("Speech stopped");
        currentSpeechCallbacks = null;
      }

      isSpeechQueueBusy = false;
    },
    loadAvatar({ getters, state }) {
      if (state.count === 0) {
        state.count = 1;
        var sc = document.createElement("script");
        sc.type = "text/javascript";
        //sc.src = "//vhss-d.oddcast.com/vhost_embed_functions_v4.php?acc=" + this.getSitepalID + "&js=0";
        sc.src =
          "//vhss-d.oddcast.com/vhost_embed_functions_v4.php?acc=" +
          getters.getAvatarDetails.sitepalId +
          "&js=0";

        //sc.src = this.getSitepalID
        sc.onload = () => {
          setTimeout(() => {
            if (typeof AC_VHost_Embed == "function") {
              var script = document.createElement("script");
              script.type = "text/javascript";
              script.innerHTML =
                "AC_VHost_Embed(7295865,960,960,'',1,0," +
                getters.getAvatarDetails.avatarId +
                ",0,1,1,'RwZIxMafWux6BWjhauvuPFZP3UcJLcqh',0, '400|300|15|15|L|T|false|0|0|0|0|0|C|0|0|0|992')";
              document.getElementById("divVHSS").appendChild(script);
            }
          }, 0);
        };
        document.head.appendChild(sc);
      }
    },
    identifyAvatarLangCodes({ getters, commit }) {
      if (getters.getCurrentLanguage === "es-US") {
        console.log(
          "Updating the language codes as language is changed to Spanish "
        );
        commit("setAvatarLangCodes", getters.getAvatarDetails.langCodes.es);
      } else {
        commit("setAvatarLangCodes", getters.getAvatarDetails.langCodes.en);
      }
    },
  },
};
